/**
 * Universal Asset Detail Page — Patch 28.
 *
 * The single canonical surface for editing ANY written asset in
 * Orkexra. Consumer pages (Content Review, Vault, Campaign Room,
 * Publishing Queue, Calendar) route into this page — there are no
 * per-page duplicate editors.
 */
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

import { useCurrentWorkspace } from "@/entities/workspace";
import { AssetEditor } from "@/features/asset-editor";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { http, ApiError } from "@/shared/api/http";
import { PremiumEmpty } from "@/shared/ui/premium-empty";
import { Skeleton } from "@/shared/ui/skeleton";


interface HeadAsset {
  permanent_asset_id: string;
  title?: string | null;
  body_markdown?: string | null;
  status?: string | null;
  content_type?: string | null;
  target_platform?: string | null;
}


interface EnvelopeHead {
  permanent_asset_id?: string | null;
  title?: string | null;
  body_markdown?: string | null;
  status?: string | null;
  content_type?: string | null;
  target_platform?: string | null;
}

interface EnvelopeResponse {
  permanent_asset_id: string;
  head?: EnvelopeHead | null;
  versions?: EnvelopeHead[];
}


// Patch 34.1.16 — Asset viewer relies on the library envelope endpoint
// (`/library/assets/{pid}`). The previous list-with-query-param call
// pointed at a route that does not exist, so opening any generated
// asset from the Vault/Review returned "Asset not found · 404". If the
// envelope is missing, we fall back to the current revision.
async function fetchHead(ws: string, pid: string): Promise<HeadAsset> {
  try {
    const env = await http<EnvelopeResponse>(`/workspaces/${ws}/library/assets/${encodeURIComponent(pid)}`);
    const head = env.head ?? env.versions?.[0] ?? null;
    if (head) {
      return {
        permanent_asset_id: env.permanent_asset_id ?? pid,
        title: head.title ?? null,
        body_markdown: head.body_markdown ?? "",
        status: head.status ?? "draft",
        content_type: head.content_type ?? null,
        target_platform: head.target_platform ?? null,
      };
    }
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) throw err;
    // 404 from envelope → try the revisions fallback below.
  }
  const rev = await http<{ body_markdown?: string; title?: string; status?: string }[]>(`/workspaces/${ws}/assets/${pid}/revisions`);
  const current = rev.find((r) => (r as unknown as { is_current?: boolean }).is_current) ?? rev[0];
  if (!current) {
    throw new ApiError({ code: "asset_not_found", status: 404, message: "This asset has not been persisted yet." });
  }
  return {
    permanent_asset_id: pid,
    body_markdown: current.body_markdown ?? "",
    title: current.title ?? null,
    status: current.status ?? "draft",
  };
}


export default function AssetDetailPage(): JSX.Element {
  const { permanentAssetId } = useParams();
  const workspace = useCurrentWorkspace();

  if (!workspace) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <PremiumEmpty testId="asset-detail-empty" title="Select a workspace"
          description="Choose a workspace to view this asset." />
      </div>
    );
  }
  if (!permanentAssetId) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <PremiumEmpty testId="asset-detail-missing" title="Asset ID missing"
          description="The URL is incomplete — no asset id was provided." />
      </div>
    );
  }
  return <AssetDetailInner ws={workspace.id} pid={permanentAssetId} />;
}


function AssetDetailInner({ ws, pid }: { ws: string; pid: string }): JSX.Element {
  const q = useQuery({
    queryKey: ["asset-detail-head", ws, pid],
    queryFn: () => fetchHead(ws, pid),
  });

  if (q.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <Skeleton className="mb-6 h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <PremiumEmpty testId="asset-detail-error" title="Asset not found"
          description={q.error instanceof Error ? q.error.message : "Try returning to the library."} />
      </div>
    );
  }
  const asset = q.data;
  const status = (asset.status ?? "draft").toLowerCase();

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8 md:px-8" data-testid={`asset-detail-page-${pid}`}>
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/vault" data-testid="asset-detail-back">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] uppercase tracking-widest">
            {asset.content_type ?? "asset"}
          </Badge>
          <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${
            status === "published" ? "border-emerald-500/40 text-emerald-300" :
            status === "scheduled" ? "border-sky-500/40 text-sky-300" :
            status === "approved" ? "border-emerald-500/40 text-emerald-300" :
            status === "needs_review" ? "border-amber-500/40 text-amber-300" :
            "text-muted-foreground"
          }`}>
            {status.replace("_", " ")}
          </Badge>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {pid}
          </span>
        </div>
      </div>

      <Card className="border-border/60 bg-card/70">
        <CardHeader>
          <CardTitle className="font-display text-2xl">
            {asset.title || "Untitled asset"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AssetEditor
            workspaceId={ws}
            permanentAssetId={pid}
            initialBody={asset.body_markdown ?? ""}
            initialStatus={asset.status ?? "draft"}
            initialTitle={asset.title ?? ""}
            contextLabel="Universal editor · Patch 28"
          />
        </CardContent>
      </Card>
    </div>
  );
}
